import { useState } from "react";
import { Info, Save, RotateCcw } from "lucide-react";
import AppCard from "../../components/AppCard";
import Button from "../../components/ui/Button";

const toneOptions = [
  { value: "professional", label: "Professional", description: "Formal, business-appropriate language" },
  { value: "friendly", label: "Friendly", description: "Warm, conversational tone" },
  { value: "concise", label: "Concise", description: "Brief, to-the-point responses" },
  { value: "detailed", label: "Detailed", description: "Thorough explanations with context" },
] as const;

const experienceLevels = [
  { value: "beginner", label: "Beginner", description: "Simple terms, more explanations" },
  { value: "intermediate", label: "Intermediate", description: "Standard business terminology" },
  { value: "advanced", label: "Advanced", description: "Technical language, less hand-holding" },
] as const;

type Tone = (typeof toneOptions)[number]["value"];
type ExperienceLevel = (typeof experienceLevels)[number]["value"];

const defaultState = {
  customInstructions: "",
  tone: "professional" as Tone,
  experienceLevel: "intermediate" as ExperienceLevel,
};

export default () => {
  const [customInstructions, setCustomInstructions] = useState(defaultState.customInstructions);
  const [tone, setTone] = useState<Tone>(defaultState.tone);
  const [experienceLevel, setExperienceLevel] = useState<ExperienceLevel>(defaultState.experienceLevel);
  const [saved, setSaved] = useState(false);

  const hasChanges =
    customInstructions !== defaultState.customInstructions ||
    tone !== defaultState.tone ||
    experienceLevel !== defaultState.experienceLevel;

  const handleSave = () => {
    setSaved(true);
    setTimeout(() => setSaved(false), 2000);
  };

  const handleReset = () => {
    setCustomInstructions(defaultState.customInstructions);
    setTone(defaultState.tone);
    setExperienceLevel(defaultState.experienceLevel);
  };

  return (
    <div className="space-y-6">
      <div className="flex items-start gap-3 rounded-2xl border border-indigo-200 bg-indigo-50 px-4 py-3">
        <Info className="mt-0.5 h-4 w-4 shrink-0 text-indigo-500" />
        <p className="text-xs font-medium text-indigo-400">
          These preferences shape how Quantira responds to your questions. Custom instructions are appended to the base prompt — core behaviour stays the same.
        </p>
      </div>

      <div className="grid gap-6 xl:grid-cols-[1fr_340px]">
        {/* Custom instructions */}
        <AppCard title="Custom Instructions" subtitle="Add specific instructions for how the AI should respond">
          <div className="space-y-2">
            <label className="ml-1 text-xs font-bold tracking-wider text-slate-400">Your Instructions</label>
            <textarea
              value={customInstructions}
              onChange={e => setCustomInstructions(e.target.value)}
              placeholder={`e.g.\n• Always normalise my P&L figures to monthly values\n• Use Australian English spelling\n• When comparing periods, always show percentage change\n• Focus on cash flow metrics over accrual-based ones`}
              rows={5}
              className="w-full resize-none rounded-xl border border-slate-200 bg-slate-50 px-4 py-3 text-sm leading-relaxed outline-none transition-colors placeholder:text-slate-300 focus:border-indigo-500"
            />
            <p className="ml-1 text-[11px] font-medium text-slate-400">
              Included with every question you ask. Be specific about your preferences.
            </p>
          </div>
        </AppCard>

        {/* Options */}
        <div className="space-y-6">
          {/* Tone */}
          <AppCard title="Response Tone" subtitle="How should the AI communicate?">
            <div className="space-y-1.5">
              {toneOptions.map(option => (
                <label
                  key={option.value}
                  className={`flex cursor-pointer items-center gap-3 rounded-xl border px-3 py-2.5 transition-all ${
                    tone === option.value
                      ? "border-indigo-200 bg-indigo-500/10 ring-1 ring-indigo-500/20"
                      : "border-slate-100 bg-slate-50/30 hover:border-slate-200"
                  }`}
                >
                  <input
                    type="radio"
                    name="tone"
                    value={option.value}
                    checked={tone === option.value}
                    onChange={() => setTone(option.value)}
                    className="sr-only"
                  />
                  <div
                    className={`flex h-3.5 w-3.5 shrink-0 items-center justify-center rounded-full border-2 transition-colors ${
                      tone === option.value ? "border-indigo-500" : "border-slate-200"
                    }`}
                  >
                    {tone === option.value && <div className="h-1.5 w-1.5 rounded-full bg-indigo-600" />}
                  </div>
                  <div>
                    <p className="text-sm font-semibold text-slate-700">{option.label}</p>
                    <p className="text-[11px] text-slate-400">{option.description}</p>
                  </div>
                </label>
              ))}
            </div>
          </AppCard>

          {/* Experience level */}
          <AppCard title="Experience Level" subtitle="Adjust the complexity of responses">
            <div className="space-y-1.5">
              {experienceLevels.map(option => (
                <label
                  key={option.value}
                  className={`flex cursor-pointer items-center gap-3 rounded-xl border px-3 py-2.5 transition-all ${
                    experienceLevel === option.value
                      ? "border-indigo-200 bg-indigo-500/10 ring-1 ring-indigo-500/20"
                      : "border-slate-100 bg-slate-50/30 hover:border-slate-200"
                  }`}
                >
                  <input
                    type="radio"
                    name="experience"
                    value={option.value}
                    checked={experienceLevel === option.value}
                    onChange={() => setExperienceLevel(option.value)}
                    className="sr-only"
                  />
                  <div
                    className={`flex h-3.5 w-3.5 shrink-0 items-center justify-center rounded-full border-2 transition-colors ${
                      experienceLevel === option.value ? "border-indigo-500" : "border-slate-200"
                    }`}
                  >
                    {experienceLevel === option.value && <div className="h-1.5 w-1.5 rounded-full bg-indigo-600" />}
                  </div>
                  <div>
                    <p className="text-sm font-semibold text-slate-700">{option.label}</p>
                    <p className="text-[11px] text-slate-400">{option.description}</p>
                  </div>
                </label>
              ))}
            </div>
          </AppCard>
        </div>
      </div>

      {/* Save bar */}
      <div className="flex items-center justify-between rounded-2xl border border-slate-200 bg-white px-5 py-3 shadow-sm">
        <p className="text-xs font-bold text-slate-400">
          {saved ? (
            <span className="text-emerald-600">Preferences saved</span>
          ) : hasChanges ? (
            "You have unsaved changes"
          ) : (
            "No changes"
          )}
        </p>
        <div className="flex gap-2">
          <Button variant="ghost" size="sm" onClick={handleReset} disabled={!hasChanges}>
            <RotateCcw className="h-3 w-3" />
            Reset
          </Button>
          <Button size="sm" onClick={handleSave} disabled={!hasChanges}>
            <Save className="h-3 w-3" />
            Save
          </Button>
        </div>
      </div>
    </div>
  );
};
